/* tslint:disable:no-bitwise one-variable-per-declaration no-console */

export interface BleDevice {
  name: string
  id: string
  rssi: number
  advertising: any
}

import easyBtSdk from '@/lib/inversBtSdk/easyBtSdk'

const SERVICE_CAR_CONTROL = '869CEF80-B058-11E4-AB27-00025B03E1F4'
const CHARACTERISTIC_AUTHORIZE_PHONE = '869CEF82-B058-11E4-AB27-00025B03E1F4'
const CHARACTERISTIC_COMMAND_PHONE = '869CEF84-B058-11E4-AB27-00025B03E1F4'

const SERVICE_CAR_INFO = '869CEFA0-B058-11E4-AB27-00025B03E1F4'
const CHARACTERISTIC_COMMAND_CHALLENGE = '869CEFA2-B058-11E4-AB27-00025B03E1F4'

/**
 * Computes a HMAC-SHA1 code.
 *
 * @param {string} k Secret key.
 * @param {string} d Data to be hashed.
 * @return {string} The hashed string.
 */

function decode(k: string, d: string) {
  d = atob(d)
  const s = []
  let j = 0, i = 0
  let x
  let res = ''
  for (let i1 = 0; i1 < 256; i1++) {
    s[i1] = i1
  }
  for (let i2 = 0; i2 < 256; i2++) {
    j = (j + s[i2] + k.charCodeAt(i2 % k.length)) % 256
    x = s[i2]
    s[i2] = s[j]
    s[j] = x
  }
  j = 0
  for (let y = 0; y < d.length; y++) {
    i = (i + 1) % 256
    j = (j + s[i]) % 256
    x = s[i]
    s[i] = s[j]
    s[j] = x
    res += String.fromCharCode(d.charCodeAt(y) ^ s[(s[i] + s[j]) % 256])
  }
  return res
}

function b64_hmac_sha1(k: string, d: string): string {
  // heavily optimized and compressed version of http://pajhome.org.uk/crypt/md5/sha1.js
  // _p = b64pad, _z = character size; not used here but I left them available just in case

  const _p = '='

  const _z = 8

  function _f(t: number, b: number, c: number, d: number) {
    if (t < 20) {
      return (b & c) | ((~b) & d)
    }
    if (t < 40) {
      return b ^ c ^ d
    }
    if (t < 60) {
      return (b & c) | (b & d) | (c & d)
    }
    return b ^ c ^ d
  }

  function _k(t: number) {
    return (t < 20) ? 1518500249 : (t < 40) ? 1859775393 : (t < 60) ? -1894007588 : -899497514
  }

  function _s(x: number, y: number) {
    const l = (x & 0xFFFF) + (y & 0xFFFF), m = (x >> 16) + (y >> 16) + (l >> 16)
    return (m << 16) | (l & 0xFFFF)
  }

  function _r(n: number, c: number) {
    return (n << c) | (n >>> (32 - c))
  }

  function _c(x: number[], l: number) {
    x[l >> 5] |= 0x80 << (24 - l % 32)
    x[((l + 64 >> 9) << 4) + 15] = l
    const w = [80]
    let a = 1732584193, b = -271733879, c = -1732584194, d = 271733878, e = -1009589776
    for (let i = 0; i < x.length; i += 16) {
      const o = a, p = b, q = c, r = d, s = e
      for (let j = 0; j < 80; j++) {
        if (j < 16) {
          w[j] = x[i + j]
        } else {
          w[j] = _r(w[j - 3] ^ w[j - 8] ^ w[j - 14] ^ w[j - 16], 1)
        }
        const t = _s(_s(_r(a, 5), _f(j, b, c, d)), _s(_s(e, w[j]), _k(j)))
        e = d
        d = c
        c = _r(b, 30)
        b = a
        a = t
      }
      a = _s(a, o)
      b = _s(b, p)
      c = _s(c, q)
      d = _s(d, r)
      e = _s(e, s)
    }
    return [a, b, c, d, e]
  }

  function _b(s: string) {
    const b: number[] = [], m = (1 << _z) - 1
    for (let i = 0; i < s.length * _z; i += _z) {
      b[i >> 5] |= (s.charCodeAt(i / 8) & m) << (32 - _z - i % 32)
    }
    return b
  }

  function _h(k: string, d: string) {
    let b = _b(k)
    if (b.length > 16) {
      b = _c(b, k.length * _z)
    }
    const p = [16], o = [16]
    for (let i = 0; i < 16; i++) {
      p[i] = b[i] ^ 0x36363636
      o[i] = b[i] ^ 0x5C5C5C5C
    }
    const h = _c(p.concat(_b(d)), 512 + d.length * _z)
    return _c(o.concat(h), 512 + 160)
  }

  function _n(b: number[]) {
    const t = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'
    let s = ''
    for (let i = 0; i < b.length * 4; i += 3) {
      const r = (((b[i >> 2] >> 8 * (3 - i % 4)) & 0xFF) << 16) | (((b[i + 1 >> 2] >> 8 * (3 - (i + 1) % 4)) & 0xFF) << 8) | ((b[i + 2 >> 2] >> 8 * (3 - (i + 2) % 4)) & 0xFF)
      for (let j = 0; j < 4; j++) {
        if (i * 8 + j * 6 > b.length * 32) {
          s += _p
        } else {
          s += t.charAt((r >> 6 * (3 - j)) & 0x3F)
        }
      }
    }
    return s
  }

  function _x(k: string, d: string) {
    return _n(_h(k, d))
  }

  return _x(k, d)
}

// Data conversion function
function stringToB64(data: string) {
  const bytes = bluetoothle.stringToBytes(data)
  return bluetoothle.bytesToEncodedString(bytes)
}

function intArrayToB64(data: number[]) {
  return stringToB64(intArrayToString(data))
}

function intArrayToString(data: number[]) {
  let res = ''
  data.forEach((i: number) => {
    res += String.fromCharCode(i)
  })
  return res
}

function B64toIntArray(data: string) {
  const s = atob(data), d = []
  for (let i = 0; i < s.length; i++) {
    d[i] = s.charCodeAt(i)
  }
  return d
}

// End data conversion function

function stringToIntArray(str: string) {
  const bag = []
  for (let c = 0; c < str.length; c++) {
    bag.push(str.charCodeAt(c))
  }
  return bag
}

function tokenProtocol(input: number[]) {
  const bag = []
  while (input.length > 0) {
    bag.push([(input.length & 0xFF), ((input.length >> 8) & 0xFF), ...input.slice(0, 18)])
    input = input.slice(18, input.length)
  }
  return bag
}

// End utility

function sendChainData(data: any, index: number, id: string, service: string, char: any, success: () => void, failure: (error?: string) => void) {
  const value = data && data[index] ? intArrayToB64(data[index]) : ''
  console.log('sending', index, value)

  bluetoothle.write(
    () => {
      console.log('sent', index)
      if (index < data.length - 1) {
        sendChainData(data, index + 1, id, service, char, success, failure)
      } else {
        success()
      }
    },
    (d) => {
      failure(d.message)
      console.error('Cannot send to device', d)
    },
    {
      value,
      characteristic: char,
      address: id,
      service: service,
    },
  )
}

export default {
  connectAndDo: function (address: string, doFunction: () => void, failure: (error?: string) => void) {
    // Connect to device
    console.log('CONNECTING...')

    const deviceParams = {address}, tries = 5

    const doDiscover = function (discovered: () => void) {
      bluetoothle.isDiscovered(
        (success) => {
          if (success.isDiscovered) {
            discovered()
          } else {
            bluetoothle.discover(
              () => {
                discovered()
              },
              (error) => {
                console.log('DISCOVER ERROR DISCOVERING', error)
              },
              deviceParams,
            )
          }
        },
        (error) => {
          console.log('DISCOVER ERROR CHECKING', error)
          if ((error as any).error === 'isNotConnected') {
            bluetoothle.reconnect(() => {
              doDiscover(discovered)
              console.log('RECONNECTED')
            }, (error2) => {
              failure(error2.message)
            }, deviceParams)
          } else {
            failure(error.message)
          }
        },
        deviceParams)
    }

    bluetoothle.initialize((s) => {
      console.log('DEVICE INIT: ' + s.status)

      bluetoothle.isConnected(
        () => {
          console.log('ALREADY CONNECTED')
          doDiscover(() => {
            doFunction()
            console.log('discovered')
          })
        },
        // Device is not connected
        () => {
          bluetoothle.connect(
            (success) => {
              console.log('CONNECT SUCCESS', success)
              doDiscover(() => {
                doFunction()
              })
            },
            (error) => {
              console.log('CONNECT ERROR', error)
              failure(error.message)
            },
            deviceParams,
          )
        }, deviceParams)
    })
  },

  sendCommandToInvers(id: string, authToken: string, sessionToken: string, command: string, logger: (message: string) => void, success: () => void, failure: (error?: string) => void) {
    let commandBytes: number[] = []
    switch (command) {
      case 'start':
        commandBytes = [0x02 + 0x08]
        break
      case 'end':
        commandBytes = [0x01 + 0x04]
        break
    }

    let auth: number[][]
    let sessionBytes: number[]

    try {
      auth = tokenProtocol(stringToIntArray(atob(authToken)))
      sessionBytes = stringToIntArray(atob(sessionToken))
    } catch (e) {
      failure('bluetooth.error.possible_pin_error')
      return
    }

    console.log('before connectAndDo', authToken, '->', auth)

    this.connectAndDo(id, () => {
      sendChainData(auth, 0, id, SERVICE_CAR_CONTROL, CHARACTERISTIC_AUTHORIZE_PHONE, () => {
        logger('CONNECTED TO DEVICE')
        // Read challenge
        logger('REQUEST CHALLENGE...')
        bluetoothle.read(
          (r) => {
            logger('...challenge retrieved')
            // console.log('CHAR READ ', r.value, B64toIntArray(r.value))

            const value = commandBytes
            const commandChallenge = B64toIntArray(r.value)
            const base64DecodedSessionKey = sessionBytes

            const bytes = new Uint8Array(10 - value.length)

            const command = []
            command.push(...value, ...bytes)

            const valueCommand = []
            valueCommand.push(...command, ...commandChallenge)

            const responseHMAC = B64toIntArray(b64_hmac_sha1(intArrayToString(base64DecodedSessionKey), intArrayToString(valueCommand)))

            const result = []
            result.push(...command, ...responseHMAC.slice(0, 10))

            bluetoothle.write(
              () => {
                logger('wrote command')
                success()
                bluetoothle.disconnect(() => {
                  logger('disconnected')
                  bluetoothle.close(() => {
                    logger('closed connection')
                  }, () => {
                    logger('close error')
                  }, {
                    address: id,
                  })
                }, () => {
                  logger('disconnect failed')
                }, {
                  address: id,
                })
              },
              (error) => {
                console.error('Cannot send to device', error)
                failure(error.message)
              },
              {
                value: intArrayToB64(result),
                characteristic: CHARACTERISTIC_COMMAND_PHONE,
                address: id,
                service: SERVICE_CAR_CONTROL,
              },
            )
          },
          (error) => {
            failure(error.message)
            console.error('cannot read char')
          },
          {
            characteristic: CHARACTERISTIC_COMMAND_CHALLENGE,
            address: id,
            service: SERVICE_CAR_INFO,
          },
        )
      }, failure)
    }, failure)
  },

  test(
    pin: string, cmd: string, qnr: string, token: string, sessionKey: string,
    logger: (message: string) => void, success: () => void, failure: (error?: string) => void) {

    logger('start')
    token = decode(pin, token)
    sessionKey = decode(pin, sessionKey)

    easyBtSdk.searchByName(qnr, (address) => {
      logger(qnr + ' > ' + address)
      this.sendCommandToInvers(address, token, sessionKey, cmd, logger, success, failure)
    }, failure)
  },
}
